import { BsThreeDotsVertical } from "react-icons/bs";
import { useMemo, useState, useRef, useEffect } from "react";
import { PopupButton } from "../../components/Navbar/style";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { blockDriver, fetchAcceptedDrivers } from "../../services/drivers";
import Avatar from "../../components/Avatar";  
import Spinner from "../../components/Spinner";
import DataTable from "../../components/DataTable";
import StarRating from "../../components/TableTitle/StarRating";
import EditDriverModal from "../../components/EditDriver";
import { TablePopup } from "../Requests/style";
import { FaBan } from "react-icons/fa6";
import toast from "react-hot-toast";
import { FiEdit } from "react-icons/fi";

const columns = [
  "ID",
  "الاسم",
  "الصورة",
  "تاريخ الانتساب",
  "تصنيف المركبة",
  "نوع السيارة",
  "رقم اللوحة",
  "رقم الهاتف",
  "الرحلات",
  "التقييم",
  "الرصيد",
  " ",
];

const CarClassDisplay = {
  VIP: "VIP",
  NORMAL: "Normal",
  BUS_7_SEATS: "باص أبو 7 راكب",
  BUS_15_SEATS: "باص أبو 15 راكب",
  BUS_30_SEATS: "باص أبو 30 راكب",
  BUS_50_SEATS: "باص أبو 50 راكب",
};

const Drivers = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, right: 0 });
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const qc = useQueryClient();
  const popupRef = useRef(null);

  const {
    data: accepted = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["drivers", "accepted"],
    queryFn: fetchAcceptedDrivers,
  });

  const blockMut = useMutation({
    mutationFn: ({ id, isBlocked }) => blockDriver(id, isBlocked),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("🚫 تم حظر السائق بنجاح ");
    },
    onError: () => toast.error("❌ فشل في حظر السائق"),
  });

  const handleDotsClick = (e, driver) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPopupPosition({
      top: rect.bottom + window.scrollY + 25,
      right: window.innerWidth - rect.right - 30,
    });
    setSelectedDriverId(driver._id);
    setShowPopup(true);
  };

  const acceptedRows = useMemo(() => {
    return accepted.map((d, id) => {
      const u = d.user ?? {};
      const v = d.vehicle ?? {};
      const formattedDate = d.created_at
        ? new Date(d.created_at).toLocaleDateString("ar-EG")
        : "—";
      const carClassLabel = CarClassDisplay[v.car_class] || "—";

      return [
        id + 1,
        u.name ?? "—",
        <Avatar img={u.profile_img} key="profile" />,
        formattedDate,
        carClassLabel,
        v.brand ?? "—",
        v.license_plate ?? "—",
        u.phone_number ?? "—",
        d.totalTrips ?? "—",
        d.avgRating ? (
          <StarRating rating={d.avgRating} />
        ) : (
          <StarRating rating={0} />
        ),
        d.totalBalance ?? "—",
        <BsThreeDotsVertical
          key="actions"
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            handleDotsClick(e, d);
          }}
        />,
        `/driver-profile/${d._id}`,
      ];
    });
  }, [accepted]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setShowPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (isLoading) return <Spinner />;
  if (isError)
    return (
      <p style={{ color: "red", textAlign: "center" }}>
        حدث خطأ أثناء تحميل البيانات: {error.message}
      </p>
    );

  return (
    <>
      {accepted.length > 0 && (
        <DataTable columns={columns} data={acceptedRows} />
      )}

      {showPopup && selectedDriverId && (
        <TablePopup
          ref={popupRef}
          style={{
            position: "absolute",
            top: `${popupPosition.top}px`,
            right: `${popupPosition.right}px`,
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            padding: "4px 0",
            zIndex: 200,
            minWidth: "180px",
          }}
        >
          <PopupButton
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 15px",
              textAlign: "right",
              width: "100%",
              border: "none",
              background: "transparent",
              color: "#5cb85c",
              fontWeight: "bold",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#e9f7ee")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
            onClick={() => {
              setShowPopup(false);
              setShowEditModal(true);
            }}
          >
            <FiEdit /> تعديل السائق
          </PopupButton>
          <div style={{ height: "1px", background: "#eee" }} />
          <PopupButton
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 15px",
              textAlign: "right",
              width: "100%",
              border: "none",
              background: "transparent",
              color: "#d9534f",
              fontWeight: "bold",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#fceaea")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
            onClick={() => {
              blockMut.mutate({ id: selectedDriverId, isBlocked: true });
              setShowPopup(false);
            }}
          >
            <FaBan /> حظر السائق
          </PopupButton>
        </TablePopup>
      )}

      {showEditModal && selectedDriverId && (
        <EditDriverModal
          driverId={selectedDriverId}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </>
  );
};

export default Drivers;